import React, { useState } from "react"
import { ContactsFiltersConditions, ConditionOperator, ContactTrackableLinkFilterType } from "@/protocols/ClientCatalogFilterProtocol"
import { FormControl, Grid, MenuItem, Select, TextField } from "@material-ui/core"
import { Autocomplete } from "@material-ui/lab"
import useDidMount from "@/hooks/useDidMount"
import ApiService from "@/services/Api"
import { Trackablelink, TrackablelinkResponse } from "@/protocols/trackableLinks"
import ErrorHandlerService from "@/services/ErrorHandler"
import { ErrorType } from "@/hooks/useValidation"

type ContactTrackableLinksFilterConditionProps = {
	condition: ContactsFiltersConditions<"contact_trackable_links">
	onChange: (condition: ContactsFiltersConditions<"contact_trackable_links">) => void
}

type ContactTrackableLinkOption = {
	operator: ConditionOperator
	label: string
}

const contactTrackableLinkFilterTypeToOptions: Record<ContactTrackableLinkFilterType, ContactTrackableLinkOption> = {
	contact_clicked_and_sent_message: {
		operator: "in",
		label: "Contato clicou no link e enviou mensagem"
	}
}

const ContactTrackableLinksFilterCondition: React.FC<ContactTrackableLinksFilterConditionProps> = ({
	condition,
	onChange
}) => {
	const [trackableLinks, setTrackableLinks] = useState<Array<Trackablelink>>([])

	const handleGetTrackableLinks = async () => {
		try {
			const { data } = await ApiService.get<TrackablelinkResponse>("/trackable-links")
			setTrackableLinks(data.trackablelinks)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	const handleFilterConditionChange = (
		updatedConditionValues: Partial<{
			slug: ContactTrackableLinkFilterType,
			trackableLinkId: number
		}>
	) => {
		const currentSlug: ContactTrackableLinkFilterType = "contact_clicked_and_sent_message"
		const newSlug = updatedConditionValues.slug ?? currentSlug
		const newTrackableLinkId = updatedConditionValues.trackableLinkId ?? condition.filterData.contact_trackable_links_id

		const { operator } = contactTrackableLinkFilterTypeToOptions[newSlug]

		onChange({
			type: "contact_trackable_links",
			operator,
			filterData: {
				contact_trackable_links_id: newTrackableLinkId,
				slug: newSlug
			}
		})
	}

	useDidMount(() => {
		handleGetTrackableLinks()
	})

	const selectedTrackableLinkId = condition.filterData.contact_trackable_links_id
	const selectedSlug: ContactTrackableLinkFilterType = "contact_clicked_and_sent_message"

	return (
		<Grid container direction="row" spacing={1}>
			<Grid item xs={7}>
				<FormControl fullWidth variant="outlined" size="small">
					<Select
						variant="outlined"
						value={selectedSlug}
						onChange={event => handleFilterConditionChange({ slug: event.target.value as ContactTrackableLinkFilterType })}
						fullWidth
					>
						{Object.entries(contactTrackableLinkFilterTypeToOptions).map(([slug, option]) => (
							<MenuItem key={slug} value={slug}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={5}>
				<Autocomplete
					id="trackable-links-outlined"
					options={trackableLinks}
					value={
						trackableLinks.find(
							link => link.id === selectedTrackableLinkId
						) ?? null
					}
					getOptionLabel={opt => opt.title}
					fullWidth
					onChange={(_, link) => {
						handleFilterConditionChange({ trackableLinkId: link?.id ?? 0 })
					}}
					renderInput={params => (
						<TextField
							{...params}
							variant="outlined"
							placeholder="Selecione o link rastreável"
						/>
					)}
					size="small"
				/>
			</Grid>
		</Grid>
	)
}

export default ContactTrackableLinksFilterCondition
